import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { CompactAlert } from '~/components/common/CompactAlert'
import { useLocationStateNotice } from '~/hooks/useLocationStateNotice'
import {
  type CsvImportKind,
  readCsvImportAppliedNotice,
} from '~/models/csvImportReturn'

type Props = {
  /** この画面が受け取る CSV の種別。 別の種別の結果は出さない */
  kind: CsvImportKind
}

/**
 * CSV を取り込んで業務画面へ戻った時に、 画面の上部へ 1 回だけ出す info の Compact Alert (× で閉じる。 spec AC-DUC-052 の情報バナー)。
 * 件数は取り込み画面が location state で渡す (`useCsvImportReturn`)。 置き方は UI 辞書
 * alert-placement.md (画面の最上部 = 呼び出し側が layout の `notices` に渡す。 業務上の優先度は「知っておけばよい」 なので、 他の通知より下)。
 * 一覧は取り込みの mutation が invalidate しているので、 戻った時に取り直される。
 */
export const CsvImportAppliedAlert = ({
  kind,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const read = useCallback(
    (state: unknown) => readCsvImportAppliedNotice(state, kind),
    [kind],
  )
  const [notice, dismiss] = useLocationStateNotice(read)

  if (notice === undefined) return null

  return (
    <CompactAlert
      status="info"
      title={t('common:upload.applied.title', notice.counts)}
    >
      <AlertDismissButton onClick={dismiss} />
    </CompactAlert>
  )
}
